import { useMemo, useState } from 'react'
import { AlertTriangle, ArrowDown, ArrowRight, LockKeyhole, MapPin } from 'lucide-react'
import { BrandMark } from '../../components/BrandMark'
import { Icon } from '../../components/Icon'
import { StatusPill } from '../../components/StatusPill'
import { categoryLabel, formatMoney, formatRelativeTime } from '../../lib/format'
import { IS_API_MODE, useAppStore } from '../../store/MockAppStore'
import type { Category, MenuItem, SalonRequest } from '../../types'
import { ConfirmationCard } from './ConfirmationCard'
import { RequestComposer } from './RequestComposer'

const categoryCopy: Record<Category, { kicker: string; description: string }> = {
  amenity: { kicker: 'A little something', description: 'Sip, refresh, and settle in.' },
  comfort: { kicker: 'Your comfort matters', description: 'Quietly let us know what you need.' },
  upgrade: { kicker: 'Elevate your ritual', description: 'Optional additions, subject to time and approval.' },
}

export function ClientConcierge({ goToStaff }: { goToStaff: () => void }) {
  const { menu, clientRequests, settings } = useAppStore()
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null)
  const [confirmation, setConfirmation] = useState<SalonRequest | null>(null)
  const [location] = useState(() => new URLSearchParams(window.location.search).get('station') || 'Station 3')

  const grouped = useMemo(
    () => (['amenity', 'comfort', 'upgrade'] as Category[]).map((category) => ({
      category,
      items: menu.filter((item) => item.category === category && item.enabled && !item.archived),
    })),
    [menu],
  )

  return (
    <div className="client-page">
      <header className="client-hero">
        <nav className="client-nav" aria-label="Main navigation">
          <BrandMark />
          <button className="location-chip" aria-label={`Current location: ${location}`}><MapPin size={14} />{location}</button>
        </nav>
        <div className="hero-stars" aria-hidden="true"><i>✦</i><i>·</i><i>✧</i></div>
        <div className="hero-copy">
          <p className="eyebrow">Welcome, lovely</p>
          <h1>A little luxury<br /><em>at your fingertips.</em></h1>
          <p>Settle in. Request anything you need without interrupting your moment.</p>
          <a className="hero-cta" href="#concierge-menu">Make a Request <ArrowDown size={17} /></a>
        </div>
      </header>

      <main id="concierge-menu" className="client-main">
        {settings.pauseNonessential && (
          <div className="pause-notice"><AlertTriangle size={18} /><div><strong>Some requests are taking a short pause.</strong><span>Comfort requests are still available.</span></div></div>
        )}

        {grouped.map(({ category, items }) => items.length > 0 && (
          <section className="menu-section" key={category} aria-labelledby={`${category}-title`}>
            <div className="section-heading">
              <div>
                <p className="eyebrow">{categoryCopy[category].kicker}</p>
                <h2 id={`${category}-title`}>{categoryLabel[category]}</h2>
                <p>{categoryCopy[category].description}</p>
              </div>
              <span className="section-number">0{category === 'amenity' ? 1 : category === 'comfort' ? 2 : 3}</span>
            </div>
            <div className="menu-grid">
              {items.map((item) => {
                const paused = settings.pauseNonessential && item.category !== 'comfort'
                return (
                  <button className="menu-card" key={item.id} onClick={() => setSelectedItem(item)} disabled={paused}>
                    <span className={`item-icon item-icon--${category}`}><Icon name={item.icon} size={23} /></span>
                    <span className="menu-card__copy">
                      <strong>{item.name}</strong>
                      <small>{item.description}</small>
                      {item.priceCents > 0 && <b>{formatMoney(item.priceCents)}</b>}
                    </span>
                    <span className="menu-card__arrow"><ArrowRight size={18} /></span>
                    {paused && <span className="paused-label">Paused</span>}
                  </button>
                )
              })}
            </div>
          </section>
        ))}

        {clientRequests.length > 0 && (
          <section className="tracking-section" aria-labelledby="tracking-title">
            <div className="section-heading section-heading--compact">
              <div><p className="eyebrow">This visit</p><h2 id="tracking-title">Your requests</h2></div>
              <LockKeyhole size={20} aria-label="Private to this session" />
            </div>
            <div className="tracking-list">
              {clientRequests.map((request) => (
                <article className="tracking-row" key={request.id}>
                  <div><strong>{request.itemName}</strong><span>{request.trackingCode} · {formatRelativeTime(request.createdAt)}</span></div>
                  <StatusPill status={request.status} />
                </article>
              ))}
            </div>
          </section>
        )}

        <aside className="urgent-note">
          <span aria-hidden="true">☾</span>
          <div><strong>A gentle note</strong><p>Requests are not monitored continuously. If you need immediate help or feel unwell, please tell your stylist directly.</p></div>
        </aside>
      </main>

      <footer className="client-footer">
        <BrandMark compact />
        <p>Made for quiet moments and thoughtful care.</p>
        <button onClick={goToStaff}>{IS_API_MODE ? 'Staff dashboard' : 'Staff preview'}</button>
      </footer>

      {selectedItem && (
        <RequestComposer
          item={selectedItem}
          location={location}
          onClose={() => setSelectedItem(null)}
          onSuccess={(request) => { setSelectedItem(null); setConfirmation(request) }}
        />
      )}
      {confirmation && <ConfirmationCard request={confirmation} onClose={() => setConfirmation(null)} />}
    </div>
  )
}
